import { ErrorHandler, importProvidersFrom, inject, Injector, provideAppInitializer, isDevMode } from "@angular/core";
import { environment } from "@env/environment";
import { AppComponent } from "./app/app.component";
import { provideAnimations } from "@angular/platform-browser/animations";
import { AvatarModule, AvatarSource } from "ngx-avatars";
import { ServiceWorkerModule } from "@angular/service-worker";
import { AppGestureConfig } from "@app/app-gesture-config";
import { HAMMER_GESTURE_CONFIG, bootstrapApplication } from "@angular/platform-browser";
import { RouterModule, provideRouter, withPreloading } from "@angular/router";
import { GlobalErrorHandlerService } from "@app/core/services/global-error-handler.service";
import { ShowOnInvalidErrorStateMatcher } from "@app/core/functions/invalid-error-state-matcher";
import { provideHttpClient } from "@angular/common/http";
import { AppInitializationFactory } from "@app/core/functions/app-initialization-factory";
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from "@angular/material/autocomplete";
import { MAT_SELECT_CONFIG } from "@angular/material/select";
import { MAT_TABS_CONFIG } from "@angular/material/tabs";
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from "@angular/material/snack-bar";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from "@angular/material/tooltip";
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter, MAT_MOMENT_DATE_FORMATS, MatMomentDateModule } from "@angular/material-moment-adapter";
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS, ErrorStateMatcher, MAT_RIPPLE_GLOBAL_OPTIONS } from "@angular/material/core";
import { I18nPaginatorService } from "@app/core/services/i18n-paginator.service";
import { MatPaginatorIntl, MAT_PAGINATOR_DEFAULT_OPTIONS } from "@angular/material/paginator";
import { LocationStrategy, HashLocationStrategy, CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from "@angular/common";
import { provideNgxMask } from "ngx-mask";
import { routes } from "@app/app-routes";
import { FormatBytesPipe } from "@app/shared/pipes/format-bytes.pipe";
import { provideMaterialCssVars } from "@app/app-libs/material-css-vars/material-css-vars";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from "@angular/material/button-toggle";
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from "@angular/material/slide-toggle";
import { AppPreloadingStrategy } from "@app/app-preload-strategy";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { provideTransloco } from "@jsverse/transloco";
import { TranslocoHttpLoader } from "./app/core/functions/transloco.loader";
import { provideTranslocoMessageformat } from "@jsverse/transloco-messageformat";

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(),
    provideRouter(routes, withPreloading(AppPreloadingStrategy)),
    provideMaterialCssVars({
      primary: "#002744",
      accent: "#ea9c12",
      warn: "#c62828",
      isDarkTheme: false,
      isAutoContrast: true
    }),
    provideNgxMask({
      decimalMarker: ".",
      thousandSeparator: ","
    }),
    provideAnimations(),
    provideTransloco({
      config: {
        availableLangs: environment.supportedLanguages,
        defaultLang: environment.defaultLanguage,
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          logMissingKey: false
        }
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoMessageformat({
      locales: environment.supportedLanguages
    }),
    DecimalPipe,
    DatePipe,
    CurrencyPipe,
    PercentPipe,
    FormatBytesPipe,
    importProvidersFrom(
      ServiceWorkerModule.register("./ngsw-worker.js", {
        enabled: environment.production
      }),
      AvatarModule.forRoot({
        sourcePriorityOrder: [AvatarSource.CUSTOM, AvatarSource.INITIALS]
      }),
      MatMomentDateModule,
      RouterModule
    ),
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: MatPaginatorIntl,
      useClass: I18nPaginatorService
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: environment.defaultLanguage
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { strict: false, useUtc: false }
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 50,
        hideDelay: 50,
        touchendHideDelay: 50,
        position: "above"
      }
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        autoFocus: true,
        disableClose: true,
        enterAnimationDuration: 0
      }
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        formFieldAppearance: "outline"
      }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: "outline",
        floatLabel: "auto"
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        verticalPosition: "bottom",
        horizontalPosition: "right",
        duration: 10000,
        announcementMessage: "Default Message"
      }
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        preserveContent: true,
        dynamicHeight: false,
        stretchTabs: false,
        animationDuration: "0"
      }
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        hideSingleSelectionIndicator: true
      }
    },
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        autoActiveFirstOption: false,
        autoSelectActiveOption: true
      }
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true
      }
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideIcon: true,
        color: undefined
      }
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        disabled: true
      }
    },
    provideAppInitializer(() => {
      const initializerFn = AppInitializationFactory(
        inject(Injector),
        inject(SystemSettingsService),
        inject(UserSettingsService),
        inject(AuthenticationService)
      );
      return initializerFn();
    }),
    {
      provide: ErrorStateMatcher,
      useClass: ShowOnInvalidErrorStateMatcher
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: AppGestureConfig
    }
  ]
  // eslint-disable-next-line no-console
}).catch((err) => console.log(err));
